<template>
  <div id="delivery-info" ref="deliveryInfo">
    <div ref="zipFormContainer" class="zip-form-container" :class="[zipFormVisible && 'active', zipError && 'error']" @click.stop="">
      <ZipForm :form-style="zipFormClass" @getZipSuccess="getZipSuccess" @getZipError="getZipError()" />
    </div>
    <template v-if="product && product.status === 'AVAILABLE'">
      <div ref="deliveryTitle" class="delivery-time-block" :class="[!showFreeShipping && 'tablet-center']">
        <div v-if="accurate" class="delivery-title">
          <div class="delivery-location" v-if="location.city && location.region">
            <span class="delivery-icon af af-delivery"></span><span>Delivered to</span>
            <a class="delivery-region" @click.prevent.stop="onRegionClick">{{ location.city }}, {{ location.region }}</a>
          </div>
          <span class="delivery-semi-colon">:</span>
          <div class="delivery-time" :class="{ flash: estimateMessageFlash }">
            <DeliveryTimeTooltip :message="etaMessage">{{ estimateMessage }}</DeliveryTimeTooltip>
          </div>
        </div>
        <div v-else-if="!loading && mounted" class="delivery-title-invalid-region">
          <span class="delivery-icon af af-delivery"></span><a class="delivery-region" @click.prevent.stop="onRegionClick">Get delivery estimate</a>
        </div>
        <div v-else>
          &nbsp;
        </div>
      </div>

      <div v-if="loading || (!inStock && !estimate.inStockIn)" class="delivery-details">
        <div class="stock-status">
          <span class="stock-title transparent">&nbsp;</span>
        </div>
      </div>
      <div v-else-if="!inStock && !estimate.inStockIn" class="delivery-details" :class="[!showFreeShipping && 'tablet-center']">
        <div class="stock-status">
          <span class="stock-title">Out of stock</span>
        </div>
      </div>
      <div v-else class="delivery-details" :class="[!showFreeShipping && 'tablet-center']">
        <div class="stock-status">
          <span v-if="inStock" class="stock-icon af af-checkmark"></span>
          <span v-if="inStock" class="stock-title">In Stock</span>
          <span v-else class="stock-title" :hidden="!estimate.inStockIn"> Back In Stock{{ estimate.inStockIn ? ` in ${estimate.inStockIn}` : '' }}</span>
        </div>
        <div v-if="showFreeShipping" class="free-shipping-container" :class="mode">
          <FreeShipping :message="shippingDiscount.details"></FreeShipping>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="delivery-time-block">&nbsp;</div>
      <div class="stock-status">Sorry, this product has been retired.</div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import { MediaUtils } from '@article/pixel';
import ZipForm from './ZipForm.vue';
import FreeShipping from './FreeShipping.vue';
import DeliveryTimeTooltip from './DeliveryTimeTooltip.vue';
import useDeliveryEstimate from '@/compositions/useDeliveryEstimate.js';
import LocationService from '@/services/LocationService.js';

export default {
  name: 'DeliveryInfo',
  components: {
    ZipForm,
    FreeShipping,
    DeliveryTimeTooltip,
  },

  props: {
    mode: {
      required: false,
      default: '',
      validator(value) {
        return ['', 'aside'].includes(value);
      },
    }
  },
  data() {
    return {
      estimateMessageFlash: false,
      zipFormClass: '',
      zipFormVisible: false,
      zipError: false,
      shouldIndentRow: false,
      mounted: false
    };
  },
  computed: {
    ...mapState('product', ['product']),
    ...mapGetters('product', [
      'price'
    ]),
    ...mapState('common', [
      'shippingDiscount', //
      'showContactBanner',
      'user',
      'etaMessage',
    ]),
    showFreeShipping() {
      return (
        !this.user.isTrade &&
        this.showContactBanner &&
        this.shippingDiscount &&
        this.shippingDiscount.details &&
        this.price.final &&
        !!this.shippingDiscount.shippingDiscountThreshold &&
        this.price.final >= this.shippingDiscount.shippingDiscountThreshold
      );
    },
    inStock() {
      if(!this.estimate) {
        return false;
      }
      return this.estimate.productIsInStock;
    }
  },
  watch: {
    estimateMessage: {
      handler(newValue, oldValue) {
        if (oldValue && newValue !== oldValue) {
          this.estimateMessageFlash = true;
          setTimeout(() => (this.estimateMessageFlash = false), 2000);
        }
      },
    }
  },
  setup() {
    const {
      loading,
      error,
      accurate,
      location,
      estimate,
      estimateMessage,
      load
    } = useDeliveryEstimate();

    return {
      loading,
      error,
      accurate,
      location,
      estimate,
      estimateMessage,
      load
    };
  },
  mounted() {
    this.load({
      product: this.product.hashKey,
      zip: LocationService.getPreferredLocation().zipcode
    });
    this.$refs.zipFormContainer?.addEventListener('touchstart', this.preventClose);

    window.addEventListener('click', this.onWindowClick, {
      capture: false,
      passive: true,
    });
    window.addEventListener('touchstart', this.onWindowClick, {
      capture: false,
      passive: true,
    });
    this.mounted = true;
  },
  destroyed() {
    this.$refs.zipFormContainer?.removeEventListener('touchstart');
    window.removeEventListener('click', this.onWindowClick);
    window.removeEventListener('touchstart', this.onWindowClick);
  },
  methods: {
    ...mapActions('common', ['loadMessaging']),
    preventClose(event) {
      event.stopPropagation();
    },
    getZipSuccess(zipCode) {
      this.zipError = false;
      this.load({
        product: this.product.hashKey,
        zip: LocationService.getPreferredLocation().zipcode
      });
      this.loadMessaging(zipCode);
    },
    getZipError() {
      this.zipError = true;
    },
    updateZipFormClass() {
      this.zipFormClass = MediaUtils.isTabletAndSmaller ? 'inline' : '';
    },
    onRegionClick() {
      this.updateZipFormClass();
      this.zipFormVisible = !this.zipFormVisible;

      if (MediaUtils.isTabletAndSmaller) {
        window.scrollTo({
          top: window.scrollY + this.$refs.deliveryInfo.getBoundingClientRect().top - 60,
          behavior: 'smooth',
        });
      }
    },
    onWindowClick() {
      this.zipError = false;
      this.updateZipFormClass();
      this.zipFormVisible = false;
    },
  },
};
</script>

<style lang="scss">
@import '~@article/pixel/dist/style/variables';
@import '~@article/pixel/dist/style/mixins';

#delivery-info {
  position: relative;

  .af-checkmark,
  .af-delivery {
    fill: var(--coral);
    color: var(--coral);
  }

  .delivery-details {
    display: flex;
    justify-content: space-between;

    &.tablet-center {
      @media (max-width: $tablet-breakpoint - 1) {
        justify-content: center;
        margin-top: 5px;
      }
    }

    @media (min-width: 500px) {
      justify-content: center;
      margin-top: 5px;
    }

    @media (min-width: $tablet-breakpoint) {
      justify-content: start;
    }
  }

  .free-shipping-container {
    margin-left: 12px;

    @media (max-width: $small-breakpoint - 1) {
      margin-left: 0px;
    }
  }

  .stock-status {
    display: flex;
    align-items: center;
    margin-bottom: var(--space-1);
    margin-top: calc(var(--space-1) / 2);

    @media (max-width: $tablet-breakpoint - 1) {
      justify-content: center;
    }

    .stock-icon {
      margin-right: 12px;
    }

    .stock-title {
      padding-top: var(--space-1);
      text-transform: uppercase;
      font-size: var(--font-size-12);
      line-height: 1.25em;
      letter-spacing: 1.4px;
      transition: opacity 100ms linear;

      &.transparent {
        opacity: 0;
      }
    }
  }

  .zip-form-container {
    z-index: 13;
    background: white;
    visibility: hidden;
    opacity: 0;
    bottom: 55px;
    left: 130px;

    transition: opacity 100ms ease-out, bottom 100ms ease-out, height 100ms ease-out, visibility 0s linear 100ms;

    &.active {
      visibility: visible;
      opacity: 1;
      bottom: 75px;
      transition: opacity 100ms ease-out, bottom 100ms ease-out, height 100ms ease-out;

      @media (max-width: $tablet-breakpoint - 1) {
        margin-bottom: 12px;
      }
    }

    &.error {
      height: 230px;
    }

    @media (max-width: $tablet-breakpoint - 1) {
      margin: auto;
      height: 0;
      margin-left: -20px;
      margin-right: -20px;
      border-top: 1px solid var(--grey-ultra-light);
      border-bottom: 1px solid var(--grey-ultra-light);

      &.active {
        height: auto;
      }
    }

    @media (min-width: $tablet-breakpoint) {
      position: absolute;
      @include overlay();
    }
  }

  .delivery-time-block {
    display: flex;

    &.tablet-center {
      @media (max-width: $tablet-breakpoint - 1) {
        justify-content: center;
      }
    }

    @media (min-width: 500px) {
      justify-content: center;
    }

    @media (min-width: $tablet-breakpoint) {
      justify-content: start;
      align-items: center;
      min-height: 26px;
      margin-bottom: 8px;
    }

    .delivery-icon {
      margin-right: 12px;
    }

    .delivery-title {
      font-size: var(--font-size-14);

      &.indented {
        text-indent: 24px;

        .delivery-location {
          margin-left: -24px;

          .delivery-icon {
            margin-left: -24px;
          }
        }
      }

      @media (max-width: $tablet-breakpoint - 1) {
        span,
        a {
          font-size: var(--font-size-12);
        }
      }

      .delivery-semi-colon {
        @media (max-width: $tablet-breakpoint - 1) {
          display: none;
        }
      }

      .delivery-location {
        display: inline-block;

        .delivery-region {
          color: var(--coral);
          font-family: var(--font-bold);
          cursor: pointer;
          border-bottom: 1px dotted var(--coral);
        }
      }

      .delivery-time {
        display: inline-block;
        padding: 0px 4px;

        &.flash {
          animation-name: flash;
          animation-duration: 500ms;
          animation-iteration-count: 2;
        }

        @keyframes flash {
          0% {
            opacity: 1;
          }

          50% {
            opacity: 0.1;
          }

          100% {
            opacity: 1;
          }
        }
      }
    }

    .delivery-title-invalid-region {
      .delivery-region {
        color: var(--coral);
        font-family: var(--font-bold);
        border-bottom: 1px dotted var(--coral);
      }
    }
  }
}
.free-shipping-container.aside {
  .free-shipping-hover {
    left: -90%;
  }
}
</style>
